/**
 * Sidebar Component - Responsive Drawer Pattern
 * Baseado em best practices 2025: Material UI drawer patterns
 *
 * Padrão Responsivo:
 * - Mobile (<768px): Temporary drawer (overlay com backdrop)
 * - Tablet (768px-1024px): Persistent drawer (toggle, push content)
 * - Desktop (>1024px): Permanent drawer (sempre visível)
 *
 * Referências:
 * - https://mui.com/material-ui/react-drawer/
 * - https://kombai.com/mui/drawer/
 */

import { Fragment, ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
}

/**
 * Sidebar com padrões adaptativos por device:
 * - Mobile: Temporary (overlay)
 * - Desktop: Permanent (sempre visível se isOpen=true)
 */
export function Sidebar({ isOpen, onClose, children, title }: SidebarProps) {
  return (
    <>
      {/* ========== MOBILE: Temporary Drawer (overlay) ========== */}
      {/* Visível apenas em telas < lg (1024px) */}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={onClose}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          {/* Drawer panel */}
          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
                {/* Header com close button */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  {title && (
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      {title}
                    </Dialog.Title>
                  )}
                  <button
                    type="button"
                    className="ml-auto rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    onClick={onClose}
                  >
                    <span className="sr-only">Fechar sidebar</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>

            {/* Spacer para clicar fora e fechar */}
            <div className="w-14 flex-shrink-0" aria-hidden="true" />
          </div>
        </Dialog>
      </Transition.Root>

      {/* ========== DESKTOP: Permanent Drawer ========== */}
      {/* Visível apenas em telas >= lg (1024px) quando isOpen=true */}
      <aside
        className={clsx(
          'hidden lg:flex flex-col w-80 bg-white border-r border-gray-200 transition-all duration-300',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:hidden'
        )}
      >
        {/* Header (opcional) */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          </div>
        )}

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>
      </aside>
    </>
  );
}

/**
 * Sidebar de Detalhes do Nó - Variante especializada
 * Aparece do lado direito (não esquerdo)
 */
interface NodeDetailsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
}

export function NodeDetailsSidebar({
  isOpen,
  onClose,
  children,
  title,
}: NodeDetailsSidebarProps) {
  return (
    <>
      {/* ========== MOBILE: Bottom Sheet ========== */}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={onClose}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          {/* Bottom sheet panel */}
          <div className="fixed inset-0 flex items-end">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
            >
              <Dialog.Panel className="relative w-full bg-white rounded-t-2xl max-h-[70vh] flex flex-col">
                {/* Handle bar (visual indicator para arrastar) */}
                <div className="flex justify-center py-2">
                  <div className="w-12 h-1 bg-gray-300 rounded-full" />
                </div>

                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  {title && (
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      {title}
                    </Dialog.Title>
                  )}
                  <button
                    type="button"
                    className="ml-auto rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    onClick={onClose}
                  >
                    <span className="sr-only">Fechar</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* ========== DESKTOP: Right-side Sidebar ========== */}
      <aside
        className={clsx(
          'hidden lg:flex flex-col w-96 bg-white border-l border-gray-200 transition-all duration-300',
          isOpen ? 'translate-x-0' : 'translate-x-full lg:hidden'
        )}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              type="button"
              className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] min-w-[44px] flex items-center justify-center"
              onClick={onClose}
            >
              <span className="sr-only">Fechar</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        )}

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>
      </aside>
    </>
  );
}
